import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Tenant {
  _id: string;
  name: string;
  logo: string;
  primary_color: string;
  secondary_colors: Array<string>;
  subdomain?: string;
  auth_image?: string[];
  paid_features: string[];
}

// Default tenant as a constant
export const DEFAULT_TENANT: Tenant = {
  _id: "",
  logo: "/src/assets/logo.svg",
  primary_color: "#006D77",
  secondary_colors: [],
  name: "Liveretail",
  auth_image: [],
  paid_features: [],
};

// Function to load tenant from localStorage
const loadTenantFromAppStorage = (): Tenant => {
  // Only run in browser environment
  if (typeof window === "undefined") {
    return DEFAULT_TENANT;
  }

  try {
    const storedTenant = localStorage.getItem("tenant");

    // If tenant doesn't exist or is empty in localStorage, use default
    if (!storedTenant || storedTenant === "{}" || storedTenant === "null") {
      // Initialize localStorage with default tenant
      localStorage.setItem("tenant", JSON.stringify(DEFAULT_TENANT));
      return DEFAULT_TENANT;
    }

    // Parse the stored tenant
    const parsedTenant = JSON.parse(storedTenant) as Tenant;
    return parsedTenant;
  } catch (error) {
    console.error("Error loading tenant from localStorage:", error);
    // Use default tenant on error
    localStorage.setItem("tenant", JSON.stringify(DEFAULT_TENANT));
    return DEFAULT_TENANT;
  }
};

// Create the slice with a function for initialState to ensure it's evaluated at runtime
const tenantSlice = createSlice({
  name: "tenant",
  // Use loadTenantFromAppStorage to initialize state from localStorage
  initialState: loadTenantFromAppStorage(),
  reducers: {
    setTenant: (state, action: PayloadAction<Tenant>) => {
      if (typeof window !== "undefined") {
        localStorage.setItem("tenant", JSON.stringify(action.payload));
      }
      // Return the new state to ensure the slice updates correctly
      return action.payload;
    },
  },
});

export const { setTenant } = tenantSlice.actions;

export default tenantSlice.reducer;
